import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("helios_user")); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("helios_token");
    if (!t) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("helios_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("helios_token");
        localStorage.removeItem("helios_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Silent JWT refresh every 24h (JWT_EXPIRE_HOURS defaults to 72h server-side)
  // Keeps users logged in indefinitely as long as they open the app periodically.
  useEffect(() => {
    const REFRESH_MS = 24 * 60 * 60 * 1000; // 24h
    const refresh = async () => {
      if (!localStorage.getItem("helios_token")) return;
      try {
        const r = await api.post("/auth/refresh");
        localStorage.setItem("helios_token", r.data.access_token);
      } catch { /* interceptor handles 401 */ }
    };
    // First refresh a bit after mount so a fresh login is not immediately re-hit
    const initial = setTimeout(refresh, 5 * 60 * 1000); // 5 min after load
    const timer = setInterval(refresh, REFRESH_MS);
    return () => { clearTimeout(initial); clearInterval(timer); };
  }, []);

  const login = useCallback(async (email, password, totp_code) => {
    const body = { email, password };
    if (totp_code) body.totp_code = totp_code;
    const r = await api.post("/auth/login", body);
    localStorage.setItem("helios_token", r.data.access_token);
    localStorage.setItem("helios_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await api.post("/auth/register", payload);
    localStorage.setItem("helios_token", r.data.access_token);
    localStorage.setItem("helios_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("helios_token");
    localStorage.removeItem("helios_user");
    setUser(null);
    window.location.href = "/login";
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
